import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Col, DatePicker, Empty, Form, Input, Row, Select, Space, Table, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ActivityStatusEnum } from '../../../../gql/typings';

type ActivityCustomFieldRow = {
  id: string;
  customFieldCode?: string;
  value?: string;
  addNewOption: boolean;
  replace: boolean;
};

export type ActivityImportMappingValue = {
  heading: string;
  typeCode: string;
  status: ActivityStatusEnum;
  startDate: string;
  endDate: string;
  responsibleUserIds: number[];
  productIds: number[];
  locationIds: number[];
  note?: string;
  customFields: Array<{
    customFieldCode: string;
    value?: string;
    addNewOption: boolean;
    replace: boolean;
  }>;
};

type ActivityImportMappingProps = {
  projectId: number;
  fileName?: string;
  setValue: (value?: ActivityImportMappingValue) => void;
};

type DataQuery = {
  viewer?: {
    user: {
      id: number;
    };
  };
  importProject?: {
    id: number;
    files: {
      nodes: Array<{
        name?: string|null;
      }>;
    };
  }|null;
  activityTypes: {
    nodes: Array<{
      code: string;
      heading: string;
      standardTimeLengthMinutes?: number|null;
    }>;
  };
  products: {
    nodes: Array<{
      id: number;
      heading: string;
      code: string;
    }>;
  };
  sites: {
    nodes: Array<{
      id: number;
      name: string;
    }>;
  };
  users: {
    nodes: Array<{
      id: number;
      fullname: string;
    }>;
  };
  customFields: {
    nodes: Array<{
      code: string;
      heading: string;
      fieldTypeEnum: string;
      allowMultiple?: boolean|null;
      allowUserCreate?: boolean|null;
    }>;
  };
};

const ActivityImportMapping: React.FC<ActivityImportMappingProps> = ({ projectId, fileName, setValue }) => {
  const { data, loading } = useQuery<DataQuery>(DATA_QUERY, {
    variables: { projectId },
  });
  const [form] = Form.useForm();
  const [customFields, setCustomFields] = useState<ActivityCustomFieldRow[]>([]);

  const defaultHeading = useMemo(() => {
    const name = fileName || data?.importProject?.files.nodes[0]?.name || '';
    if (name.startsWith('upload-')) return 'Imported activity';
    return name.replace(/\.[^.]+$/, '') || 'Imported activity';
  }, [data, fileName]);

  const customFieldsByCode = useMemo(() => new Map(
    (data?.customFields.nodes ?? []).map(field => [field.code, field])
  ), [data]);
  const isSingleValue = (field?: DataQuery['customFields']['nodes']['0']) => field?.allowMultiple !== true;
  const isMultiValue = (field?: DataQuery['customFields']['nodes']['0']) => field?.allowMultiple === true;
  const isOptionField = (field?: DataQuery['customFields']['nodes']['0']) => (
    !!field && ['CHECK', 'SELECT', 'TAG'].includes(field.fieldTypeEnum)
  );
  const canAddNewOption = (field?: DataQuery['customFields']['nodes']['0']) => (
    isOptionField(field) && field?.allowUserCreate === true
  );

  useEffect(() => {
    if (!data) return;
    const start = dayjs().second(0);
    const type = data.activityTypes.nodes[0];
    form.setFieldsValue({
      heading: defaultHeading,
      typeCode: type?.code,
      status: ActivityStatusEnum.DONE,
      dateRange: [
        start,
        start.add(type?.standardTimeLengthMinutes ?? 0, 'minute'),
      ],
      responsibleUserIds: data.viewer?.user.id ? [data.viewer.user.id] : [],
      productIds: [],
      locationIds: [],
      note: undefined,
    });
  }, [data, defaultHeading, form]);

  const emitValue = async () => {
    try {
      const values = await form.validateFields();
      const dateRange = values.dateRange as [Dayjs, Dayjs];
      const invalidCustomFields = customFields.filter(row => row.customFieldCode && !row.value?.trim());
      if (invalidCustomFields.length > 0) {
        setValue(undefined);
        return;
      }

      setValue({
        heading: values.heading,
        typeCode: values.typeCode,
        status: values.status,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        responsibleUserIds: values.responsibleUserIds ?? [],
        productIds: values.productIds ?? [],
        locationIds: values.locationIds ?? [],
        note: values.note,
        customFields: customFields
          .filter(row => row.customFieldCode && row.value?.trim())
          .map(row => ({
            customFieldCode: row.customFieldCode!,
            value: row.value,
            addNewOption: row.addNewOption,
            replace: row.replace,
          })),
      });
    } catch {
      setValue(undefined);
    }
  };

  const updateCustomField = (id: string, patch: Partial<ActivityCustomFieldRow>) => {
    setCustomFields(prev => prev.map(row => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      const selectedField = next.customFieldCode ? customFieldsByCode.get(next.customFieldCode) : undefined;
      if (patch.customFieldCode !== undefined) {
        next.replace = isSingleValue(selectedField);
      }
      if (!canAddNewOption(selectedField)) next.addNewOption = false;
      if (isSingleValue(selectedField)) next.replace = true;
      return next;
    }));
  };

  const removeCustomField = (id: string) => {
    setCustomFields(prev => prev.filter(row => row.id !== id));
  };

  useEffect(() => {
    emitValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields]);

  return (
    <div>
      <h2>Activity details</h2>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={emitValue}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="heading" label="Heading" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="typeCode" label="Activity type" rules={[{ required: true }]}>
              <Select
                loading={loading}
                options={(data?.activityTypes.nodes ?? []).map(type => ({
                  value: type.code,
                  label: type.heading,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="status" label="Activity status" rules={[{ required: true }]}>
              <Select
                options={Object.values(ActivityStatusEnum).map(status => ({
                  value: status,
                  label: status,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="dateRange" label="Date / start / end" rules={[{ required: true }]}>
              <DatePicker.RangePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="responsibleUserIds" label="Responsible user" rules={[{ required: true }]}>
              <Select
                mode="multiple"
                loading={loading}
                options={(data?.users.nodes ?? []).map(user => ({
                  value: user.id,
                  label: user.fullname,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="productIds" label="Product">
              <Select
                mode="multiple"
                loading={loading}
                options={(data?.products.nodes ?? []).map(product => ({
                  value: product.id,
                  label: product.heading,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="locationIds" label="Location">
              <Select
                mode="multiple"
                loading={loading}
                showSearch
                optionFilterProp="label"
                options={(data?.sites.nodes ?? []).map(site => ({
                  value: site.id,
                  label: site.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="note" label="Note">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <h2>Activity own fields</h2>
      <Space direction="vertical" size={0} style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">
          <b>New option:</b>
          {' '}
          checked creates new options; unchecked uses existing options only.
        </Typography.Text>
        <Typography.Text type="secondary">
          <b>Replace:</b>
          {' '}
          checked replaces existing activity values; unchecked adds to them. Always checked for single-value fields.
        </Typography.Text>
      </Space>
      <Table<ActivityCustomFieldRow>
        dataSource={customFields}
        pagination={false}
        rowKey="id"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No data"
              style={{ margin: '10px 0' }}
            />
          ),
        }}
        columns={[
          {
            title: 'Own field',
            render: (_, row) => (
              <Select
                value={row.customFieldCode}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                options={(data?.customFields.nodes ?? []).map(field => ({
                  value: field.code,
                  label: field.heading,
                }))}
                onChange={customFieldCode => updateCustomField(row.id, { customFieldCode })}
              />
            ),
          },
          {
            title: 'Value',
            render: (_, row) => (
              <Input
                value={row.value}
                onChange={event => updateCustomField(row.id, { value: event.target.value })}
              />
            ),
          },
          {
            title: 'New option',
            width: 120,
            render: (_, row) => {
              const selectedField = row.customFieldCode ? customFieldsByCode.get(row.customFieldCode) : undefined;
              const disabled = !canAddNewOption(selectedField);

              return (
                <Checkbox
                  checked={row.addNewOption && !disabled}
                  disabled={disabled}
                  onChange={event => updateCustomField(row.id, { addNewOption: event.target.checked })}
                />
              );
            },
          },
          {
            title: 'Replace',
            width: 100,
            render: (_, row) => {
              const selectedField = row.customFieldCode ? customFieldsByCode.get(row.customFieldCode) : undefined;
              const disabled = !selectedField || isSingleValue(selectedField);

              return (
                <Checkbox
                  checked={selectedField ? isSingleValue(selectedField) || row.replace : false}
                  disabled={disabled}
                  onChange={event => {
                    if (isMultiValue(selectedField)) updateCustomField(row.id, { replace: event.target.checked });
                  }}
                />
              );
            },
          },
          {
            title: '',
            width: 100,
            render: (_, row) => (
              <Button danger onClick={() => removeCustomField(row.id)}>
                Remove
              </Button>
            ),
          },
        ]}
      />
      <Button
        style={{ marginTop: 12 }}
        onClick={() => setCustomFields(prev => [
          ...prev,
          {
            id: `${Date.now()}-${prev.length}`,
            addNewOption: false,
            replace: false,
          },
        ])}
      >
        Add own field
      </Button>
    </div>
  );
};

const DATA_QUERY = gql`
  query ActivityImportMappingData($projectId: Int!) {
    viewer {
      user {
        id
      }
    }
    importProject(id: $projectId) {
      id
      files {
        nodes {
          name
        }
      }
    }
    activityTypes {
      nodes {
        code
        heading
        standardTimeLengthMinutes
      }
    }
    products(criteria: {}) {
      nodes {
        id
        code
        heading
      }
    }
    sites(criteria: { fetchSize: { limit: 10000 } }) {
      nodes {
        id
        name
      }
    }
    users(criteria: { fetchSize: { limit: 10000 } }) {
      nodes {
        id
        fullname
      }
    }
    customFields(criteria: { entityType: ACTIVITY, isActive: true }) {
      nodes {
        code
        heading
        fieldTypeEnum
        allowMultiple
        allowUserCreate
      }
    }
  }
`;

export default ActivityImportMapping;
